import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Sidebar } from '@/components/layout/Sidebar';
import { LoadingOverlay } from '@/components/ui';
import { NotificationSystem } from '@/components/NotificationSystem';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/api/client';
import { cn } from '@/lib/utils';

// Lazy load pages for code splitting
const DashboardPage = React.lazy(() => import('@/features/dashboard/DashboardPage'));
const CollectionPage = React.lazy(() => import('@/features/collection/CollectionPage'));
const ReviewPage = React.lazy(() => import('@/features/review/ReviewPage'));
const ReviewDetailPage = React.lazy(() => import('@/features/review/ReviewDetailPage'));
const ApprovalPage = React.lazy(() => import('@/features/approval/ApprovalPage'));
const SearchPage = React.lazy(() => import('@/features/search/SearchPage'));
const ConfigPage = React.lazy(() => import('@/features/config/ConfigPage'));
const LoginPage = React.lazy(() => import('@/features/auth/LoginPage'));
const AIChatPage = React.lazy(() => import('@/features/chat/AIChatPage'));
const CompanySearchPage = React.lazy(() => import('@/features/company/CompanySearchPage'));
const CompanyDetailPage = React.lazy(() => import('@/features/company/CompanyDetailPage'));
const AuditLogPage = React.lazy(() => import('@/features/audit/AuditLogPage'));

// Create Query Client with production-grade config
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes - data considered fresh
      gcTime: 1000 * 60 * 30, // 30 minutes - keep in cache (formerly cacheTime)
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: true, // Refetch when tab becomes active (like Facebook)
      refetchOnReconnect: true, // Refetch when network reconnects
      refetchOnMount: 'always', // Always check for updates on mount
    },
    mutations: {
      retry: 1,
    },
  },
});

// Protected route wrapper with token validation
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isValidating = useAuthStore((s) => s.isValidating);
  const token = useAuthStore((s) => s.token);
  const validateAndSetAuth = useAuthStore((s) => s.validateAndSetAuth);
  
  // Validate token on mount if we have one stored
  useEffect(() => {
    const validateToken = async () => {
      if (token && isValidating) {
        try {
          // Call /auth/me to validate token with timeout
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 5000)
          );
          
          await Promise.race([authApi.me(), timeoutPromise]);
          // Token is valid
          validateAndSetAuth(true);
        } catch (error) {
          // Token is invalid or expired or timeout
          console.warn('Token validation failed, logging out:', error);
          validateAndSetAuth(false);
        }
      }
    };
    
    validateToken();
  }, [token, isValidating, validateAndSetAuth]);
  
  // Show loading while validating token (only if we don't have auth yet)
  // TEMPORARILY DISABLED for debugging overlay issue
  // if (isValidating && token && !isAuthenticated) {
  //   return <LoadingOverlay />;
  // }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// Main layout with sidebar
function MainLayout({ children }: { children: React.ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  return (
    <div className="min-h-screen bg-surface dark:bg-surface-dark">
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main
        className={cn(
          'h-screen overflow-y-auto transition-[padding] duration-300',
          sidebarCollapsed ? 'pl-16' : 'pl-64'
        )}
      >
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

// App component
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<LoadingOverlay />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/collection" element={<CollectionPage />} />
                      <Route path="/review" element={<ReviewPage />} />
                      <Route path="/review/:id" element={<ReviewDetailPage />} />
                      <Route path="/approval" element={<ApprovalPage />} />
                      <Route path="/search" element={<SearchPage />} />
                      <Route path="/config" element={<ConfigPage />} />
                      <Route path="/chat" element={<AIChatPage />} />
                      <Route path="/company" element={<CompanySearchPage />} />
                      <Route path="/company/:taxCode" element={<CompanyDetailPage />} />
                      <Route path="/audit" element={<AuditLogPage />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </MainLayout>
                  {/* Real-time notification system */}
                  <NotificationSystem />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
